import {useCallback, useEffect, useState, ReactNode} from 'react'
import {useRouter} from 'next/router'
import {getCookie, setCookie} from 'cookies-next'
import _ from 'lodash'
import type {GetServerSidePropsContext, GetServerSidePropsResult} from 'next'
// Custom hooks
import {useBoundRoute} from '../../hooks/use-bound-route'
import {useInterval} from '../../hooks/use-interval'
// Contexts
import {useBallotContext} from '../../context/ballot'
// Layouts
import PagePasswordInput from '../../components/layout/page-password-input'
import PageSimplePage from '../../components/layout/page-simple-page'
import Section from '../../components/layout/section'
// Components
import BoundLink from '../../components/bound-link'
import FooterAdContents from '../../components/ads/footer-ad-contents'
import ModalContainer from '../../components/layout/modal-container'
import ClientHead from '../../components/client-head'
import ClientMeta from '../../components/client-meta'
import ClientText from '../../components/client-text'
import GroupHero from '../../components/group-hero'
import GroupList from '../../components/group-list'
import LoadingIndicator from '../../components/loading-indicator'
import Navbar from '../../components/navbar'
import QuestionList from '../../components/question-list'
import SponsorDisplay from '../../components/sponsor-display'
import StartButton from '../../components/start-button'
// Utils
import {calculateNextAdByPool} from '../../utils/array-helpers'
import {accessBallotIsPublic, accessBallotIsPublished} from '../../utils/ballot-access'
import {
  calculateBallotPassword,
  calculateFirstQuestionUrl,
  calculateGroupByBallotAndGroupSlug,
  calculateHasOnlyOneGroup,
  calculateSponsorshipAdPoolByBallot,
  calculateFooterAdPoolByBallotAndGroup,
} from '../../utils/ballot-calculators'
import {recordAdAndPageViews} from '../../utils/analytics'
import {convertHoursToSeconds} from '../../utils/number-helpers'
import {loadBallotByHost} from '../../utils/prerender-helpers'
// Styles
import pageStyles from '../../components/layout/page.module.scss'
// Consts
import {backupBallot} from '../../consts/ballots'
// Types
import type {Ballot, ParentCategory, SponsorshipAd} from '../../types/ballot'
import type {Customer} from '../../types/customer'

interface GroupShowProps {
  initBasicBallot: Ballot
  dataFetchingError?: {message: string} | null
}

const GroupShow = ({
  initBasicBallot,
  dataFetchingError,
}: GroupShowProps) => {
  if (dataFetchingError) return <PageSimplePage title={dataFetchingError.message} isErrorPage={true} />

  // Not yet loading fullBallot
  const {fullBallot, isLoadingFullBallot} = useBallotContext()

  const boundRoute = useBoundRoute()
  const router = useRouter()

  const [ballotIsPublished, setBallotIsPublished] = useState<boolean>(false)
  const [ballotIsPublic, setBallotIsPublic] = useState<boolean>(false)
  const [allGroups, setAllGroups] = useState<ParentCategory[] | null>(null)
  const [currentGroup, setCurrentGroup] = useState<ParentCategory | null>(null)
  // TODO: Double-check if any of these need to be state or if there's any performance hits
  const [clientHeaderHtml, setClientHeaderHtml] = useState<string | false | any[]>([])
  const [footerAd, setFooterAd] = useState<SponsorshipAd | null>(null)
  const [footerAdPool, setFooterAdPool] = useState<SponsorshipAd[]>([])
  const [footerAdIndex, setFooterAdIndex] = useState<number>(0)
  const [footerAdTimerDelay, setFooterAdTimerDelay] = useState<number | null>(null)

  const [sponsorshipAdPool, setSponsorshipAdPool] = useState<SponsorshipAd[]>([])

  const [currentWindowWidth, setCurrentWindowWidth] = useState<number>(480)
  const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
  const sendSidebarIsOpen = (value: boolean): void => setSidebarIsOpen(value)

  const [ballotPassword, setBallotPassword] = useState<string | null>(calculateBallotPassword(initBasicBallot) ?? null)
  const sendHasEnteredCorrectPassword = (isCorrect: boolean): void => {
    setCookie('_uhecp', String(isCorrect), {maxAge: convertHoursToSeconds(90), path: '/'})
    location.reload()
  }

  const [modalIsIn, setModalIsIn] = useState<boolean>(false)
  const [currentModal, setCurrentModal] = useState<ReactNode | null>(null)
  const [currentModalTitle, setCurrentModalTitle] = useState<string | null>(null)
  const [currentModalHasCloseButton, setCurrentModalHasCloseButton] = useState<boolean | null>(null)
  const onUpdateModal = (
    modalContents: ReactNode | null = null, modalTitle: string | null = null, modalHasCloseButton: boolean = false,
  ) => {
    if (modalContents) {
      document.body.style.overflow = 'hidden'
      setCurrentModal(modalContents)
      setCurrentModalTitle(modalTitle)
      setCurrentModalHasCloseButton(modalHasCloseButton)
      setModalIsIn(true)
    } else {
      document.body.style.overflow = 'auto'
      setCurrentModal(null)
      setModalIsIn(false)
    }
  }

  const setAllPageState = (ballot: Ballot) => {
    if (_.isEmpty(ballot)) return

    setCookie('ballot_id', ballot._id, {maxAge: convertHoursToSeconds(90), path: '/'})

    const newBallotIsPublished = accessBallotIsPublished(ballot)
    setBallotIsPublished(newBallotIsPublished)

    const newBallotIsPublic = accessBallotIsPublic(ballot) || (getCookie('_uhecp') === 'true')
    setBallotIsPublic(newBallotIsPublic)

    const newAllGroups = ballot.parent_categories
    setAllGroups(newAllGroups)

    const routerGroupSlug = router.query?.group_slug as string | undefined
    const newCurrentGroup = routerGroupSlug ? calculateGroupByBallotAndGroupSlug(ballot, routerGroupSlug) || null : null
    setCurrentGroup(newCurrentGroup)

    const newClientHeaderHtml = (ballot.header?.header_type === 'custom') && ballot.header?.html
    setClientHeaderHtml(newClientHeaderHtml ?? false)

    const newBallotPassword = calculateBallotPassword(ballot)
    setBallotPassword(newBallotPassword ?? null)

    // Only compute group-dependent ads and analytics if the group slug resolves to a group.
    if (newCurrentGroup) {
      const newFooterAdPool = calculateFooterAdPoolByBallotAndGroup(ballot, newCurrentGroup)
      setFooterAdPool(newFooterAdPool ?? [])

      const newFooterAd = calculateNextAdByPool(newFooterAdPool ?? [])
      setFooterAd(newFooterAd)

      const newFooterAdIndex = _.findIndex(newFooterAdPool, ad => ad?._id === newFooterAd?._id)
      setFooterAdIndex(newFooterAdIndex)

      // Only initialize timer (via delay) if there are multiple ads
      let newFooterAdTimerDelay: number | null = null
      let keenFooterAds: SponsorshipAd[] = newFooterAd ? [newFooterAd] : []
      if ((ballot.general?.rotate_category_ads_text === 'rotate') && newFooterAdPool?.length) {
        newFooterAdTimerDelay = 5000
        keenFooterAds = newFooterAdPool
      }
      setFooterAdTimerDelay(newFooterAdTimerDelay)

      const newSponsorshipAdPool = calculateSponsorshipAdPoolByBallot(ballot)
      setSponsorshipAdPool(newSponsorshipAdPool ?? [])
      // TODO: Update if sponsorshipAds can rotate
      const keenSponsorshipAds = newSponsorshipAdPool

      if (router.asPath && ballot._id) {
        const pageviewProps = {
          ballotId: ballot?._id,
          group: newCurrentGroup,
          footerAds: keenFooterAds,
          sponsorshipAds: keenSponsorshipAds,
          componentName: 'GroupShow' as const,
          ballot: ballot,
        }
        recordAdAndPageViews(pageviewProps)
      }
    } else {
      // Ambiguous or unresolved slug: avoid recording analytics with a null group
      // and clear group-dependent ad state.
      setFooterAdPool([])
      setFooterAd(null)
      setFooterAdIndex(-1)
      setFooterAdTimerDelay(null)
      setSponsorshipAdPool([])
    }
  }

  useEffect(() => {
    const handleResize = () => {
      const newCurrentWindowWidth = window.innerWidth
      setCurrentWindowWidth(newCurrentWindowWidth)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!router.isReady) return
    document.documentElement.scrollTop = 0
    // Redirect if they don't belong here
    const ballotHasOnlyOneGroup = calculateHasOnlyOneGroup(fullBallot)
    if (router.isReady && ballotHasOnlyOneGroup) {
      router.push(boundRoute(calculateFirstQuestionUrl(fullBallot) ?? '/')!)
      return
    } else {
      setAllPageState(fullBallot!)
    }
  }, [
    router.isReady,
    router.push,
    boundRoute,
    fullBallot,
  ])

  const timer = useCallback(() => {
    const newFooterAd = calculateNextAdByPool(footerAdPool, footerAdIndex)
    setFooterAd(newFooterAd)

    const newFooterAdIndex = _.findIndex(footerAdPool, ad => ad?._id === newFooterAd?._id)
    setFooterAdIndex(newFooterAdIndex)
  }, [footerAdPool, footerAdIndex])
  // Uses custom hook to add timer which rotates ads (if footerAdTimerDelay !== null)
  useInterval(timer, footerAdTimerDelay)

  return <>
    <ClientMeta ballot={initBasicBallot || backupBallot} pageTitle={`${initBasicBallot?.ballot_name || backupBallot.ballot_name} - Home`} />
    {isLoadingFullBallot && <LoadingIndicator />}
    {!isLoadingFullBallot && _.isEmpty(fullBallot) && (
      <PageSimplePage title='The ballot you are looking for does not exist'>
        <h2 className='title is-4 pb-2'>
          Try returning to the home page and signing in again
        </h2>
        <BoundLink
          href='/'
          className='button is-large is-dark'
        >
          OK
        </BoundLink>
      </PageSimplePage>
    )}
    {!isLoadingFullBallot && !_.isEmpty(fullBallot) && <>
      {ballotIsPublished && <>
        {ballotIsPublic && <>
          {!currentGroup &&
            <PageSimplePage title='This group could not be found in this ballot'>
              <BoundLink
                href='/groups'
                className='button is-large is-dark'
              >
                Browse groups
              </BoundLink>
            </PageSimplePage>
          }
          {currentGroup && <>
            <ClientHead ballot={fullBallot} />
            <Navbar
              customer={(initBasicBallot.customer ?? null) as Customer | null}
              ballot={fullBallot}
              currentWindowWidth={currentWindowWidth}
              ballotIsPublished={ballotIsPublished}
              sidebarIsOpen={sidebarIsOpen}
              sendSidebarIsOpen={sendSidebarIsOpen}
              onUpdateModal={onUpdateModal}
            />
            <ClientText clientText={typeof clientHeaderHtml === 'string' ? clientHeaderHtml : undefined} isClientHeaderHtml={true}/>
            <div
              className={`
                ${pageStyles['page']}
                ${pageStyles['--has-full-width-image']}
                ${modalIsIn ? pageStyles['--modal-is-in'] : ''}
              `}
            >
              {/* Group hero image */}
              <GroupHero
                image={currentGroup?.parent_images?.groupCoverImagePath ?? ''}
                title={currentGroup?.parent_category_name ?? ''}
              />
              <ClientText clientText={currentGroup?.parent_description} />
              {/* Sponsorship ad */}
              <Section>
                <SponsorDisplay
                  ballotId={fullBallot._id}
                  sponsorshipAds={sponsorshipAdPool}
                  styleModifiers={['--ad-text-is-above']}
                />
              </Section>
              {/* Start now */}
              <Section
                styleModifiers={[
                  '--is-display-block',
                  '--is-centered-horizontally',
                ]}
              >
                <div className='buttons is-centered'>
                  <StartButton ballot={fullBallot} group={currentGroup}/>
                </div>
              </Section>
              {/* Questions list */}
              <QuestionList group={currentGroup} />
              {/* Group list */}
              {allGroups &&
                <Section styleModifiers={['--is-making-room-for-footer-ad']}>
                  <GroupList groups={allGroups} currentGroup={currentGroup} />
                </Section>
              }
              {/* Sticky footer ad */}
              {footerAd &&
                <Section
                  styleModifiers={[
                    '--is-footer',
                    '--is-fixed-to-bottom',
                    '--is-flex-row',
                    '--is-centered-horizontally',
                    '--is-full-width',
                  ]}
                >
                  <FooterAdContents
                    ballot={fullBallot}
                    group={currentGroup}
                    footerAd={footerAd}
                    currentWindowWidth={currentWindowWidth}
                  />
                </Section>
              }
            </div>
            {modalIsIn && currentModal &&
              <ModalContainer
                modalTitle={currentModalTitle ?? undefined}
                modalHasCloseButton={currentModalHasCloseButton ?? undefined}
                onUpdateModal={onUpdateModal}
              >
                {currentModal}
              </ModalContainer>
            }
          </>}
        </>}
        {!ballotIsPublic &&
          <PagePasswordInput ballotPassword={ballotPassword ?? ''} sendHasEnteredCorrectPassword={sendHasEnteredCorrectPassword} />
        }
      </>}
      {!ballotIsPublished &&
        <PageSimplePage title='This ballot is no longer active'>
          <BoundLink
            href='/'
            className='button is-large is-dark'
          >
            Read more
          </BoundLink>
        </PageSimplePage>
      }
    </>}
  </>
}

export default GroupShow

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<GroupShowProps>> => {
  const {initBasicBallot, prerenderError, customRedirect} = await loadBallotByHost(context) as any
  if (prerenderError) return prerenderError
  if (customRedirect) return customRedirect

  return {props: {initBasicBallot}}
}
